import { type Dtos, type IncomeStatementId, type SuretyAccountId } from "@inrev/common";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useRequest } from "../../utils/request";
import { incomeStatementsQueryKey } from "./financials.keys";
import { patchIncomeStatementView } from "./utils/patchIncomeStatementView";

export function useUpdateIncomeStatement(accountId: SuretyAccountId) {
	const { patch } = useRequest();
	const queryClient = useQueryClient();

	const mutation = useMutation({
		mutationKey: ["updateIncomeStatement", accountId],
		mutationFn: ({
			incomeStatementId,
			body,
		}: {
			incomeStatementId: IncomeStatementId;
			body: Dtos.Admin.IncomeStatement.Update.Request;
		}) =>
			patch<Dtos.Admin.IncomeStatement.Get.Response>(
				`/v2/admin/surety/accounts/${accountId}/income-statements/${incomeStatementId}/line-items`,
				body,
			),
		onMutate: async ({ incomeStatementId, body }) => {
			await queryClient.cancelQueries({ queryKey: incomeStatementsQueryKey(accountId) });
			const previousData = queryClient.getQueryData<Dtos.Admin.IncomeStatement.List.Response>(
				incomeStatementsQueryKey(accountId),
			);
			queryClient.setQueryData<Dtos.Admin.IncomeStatement.List.Response>(
				incomeStatementsQueryKey(accountId),
				(cached) =>
					cached?.map((s) =>
						s.id === incomeStatementId && s.view
							? { ...s, view: patchIncomeStatementView(s.view, body) }
							: s,
					),
			);
			return { previousData };
		},
		onError: (_error, _vars, context) => {
			if (context?.previousData) {
				queryClient.setQueryData(incomeStatementsQueryKey(accountId), context.previousData);
			}
		},
		onSettled: () => {
			if (queryClient.isMutating({ mutationKey: ["updateIncomeStatement", accountId] }) <= 1) {
				queryClient.invalidateQueries({ queryKey: incomeStatementsQueryKey(accountId) });
			}
		},
	});

	return {
		updateIncomeStatement: mutation.mutateAsync,
	};
}
